/** venture/InvestCertDoc.tsx — 벤처투자확인서 (11단계 법인별, 표준 문서 흐름 — 사용자 확정 2026-07-17)
 *  14단계 변경내역(ChangeDoc)과 동일 UX: 접이식 입력 → 생성 → 문서 행(미생성/변환됨/서명완료 칩 + 미리보기·원본·✍서명·⋯기타).
 *  원본 양식 PDF 오버레이 — Word 줄 없음. 입력값은 s11send에 법인별 저장(재생성 재사용). */
import React, { useState } from 'react';
import PdfEditor from '../PdfEditor';
import { genInvestCert, uploadDoc, fetchFileBlob } from './api';
import { openS3, saveS3ToProject } from './docgen';
import { getTxt, mutStore, UpLabel } from './stepcommon';
import DocFieldModal from './DocFieldModal';
import { docLoader } from './VicsDocs';
import { vicsDoc } from './vics13';
import type { Project, CoCard, DocVerMap, DocVer } from './types';

interface Props { p: Project; co: CoCard; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void }

const TYPE_DEFS = [
  { k: 'pref' as const, label: '우선주/보통주', sub: '우선주' },
  { k: 'cb' as const, label: '전환사채(CB)/신주인수권부사채(BW)', sub: '전환사채' },
  { k: 'safe' as const, label: 'SAFE/CN 등', sub: 'SAFE' },
];
const today = () => { const d = new Date(); return d.getFullYear() + '.' + String(d.getMonth() + 1).padStart(2, '0') + '.' + String(d.getDate()).padStart(2, '0') + '.'; };
/** 날짜 칸 — 숫자만 쳐도 「2026.07.01.」 (입금일 · 발급일) */
const dotDate = (v: string): string => {
  const d = v.replace(/\D/g, '').slice(0, 8);
  if (d.length < 5) return v.replace(/[^\d.]/g, '');
  return d.length === 8 ? `${d.slice(0, 4)}.${d.slice(4, 6)}.${d.slice(6, 8)}.` : d.length > 6 ? `${d.slice(0, 4)}.${d.slice(4, 6)}.${d.slice(6)}` : `${d.slice(0, 4)}.${d.slice(4)}`;
};
const box: React.CSSProperties = { background: '#F8FAFC', border: '1px solid #E4E7EF', borderRadius: 8, padding: 10, marginBottom: 8 };
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' };

export default function InvestCertDoc({ p, co, onPatch, toast }: Props) {
  const KEY = 'investcert_' + co.id;
  const fileName = '벤처투자확인서_' + (co.name || '법인') + '.pdf';
  const saved = (() => { try { return JSON.parse(getTxt(p, 's11send', co.id + '_form') || '{}'); } catch (e) { return {}; } })();
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [more, setMore] = useState(false);
  const [editUrl, setEditUrl] = useState<string | null>(null);
  const [type, setType] = useState<'pref' | 'cb' | 'safe'>(saved.type || 'cb');
  const [subType, setSubType] = useState<string>(saved.subType || '전환사채');
  const [payDate, setPayDate] = useState<string>(saved.payDate || '');
  const [shares, setShares] = useState<string>(saved.shares || '');
  const [secured, setSecured] = useState<string>(saved.secured || '부');
  const [amount, setAmount] = useState<string>(saved.amount || co.items ? saved.amount || '' : '');
  const [issueDate, setIssueDate] = useState<string>(saved.issueDate || today());
  // 입금일 = 9단계 등기부등본(전환사채 발행 후)의 사채 발행일 — 등기부를 보며 적는 창 (2026-10-09 지도사 「입금일 입력할 때 등기부등본 열어서 보면서」)
  const [regOpen, setRegOpen] = useState(false);
  const reg = vicsDoc(p, co, 'regcb');
  const cbIssue = dotDate(getTxt(p, 's8', co.id + '_b_issueDate') || '');   // 8단계 사채 서류의 발행일(있으면 바로 넣기)

  const vers: DocVer[] = (p.docver && p.docver[KEY]) || [];
  const cur: DocVer | null = vers.length ? vers[vers.length - 1] : null;
  const real = !!(cur && cur.s3pdf);

  const pickType = (k: 'pref' | 'cb' | 'safe') => {
    setType(k);
    const d = TYPE_DEFS.find((t) => t.k === k);
    if (d && (!subType || TYPE_DEFS.some((t) => t.sub === subType))) setSubType(d.sub);
  };

  const gen = async () => {
    if (busy) return;
    if (!payDate.trim()) { toast('투자금 입금일을 입력하세요 (예: 2026.07.01.)'); return; }
    if (!amount.trim()) { toast('투자금액을 입력하세요'); return; }
    setBusy('gen');
    try {
      const form = { type, subType: subType.trim(), payDate: payDate.trim(), shares: shares.trim(), secured, amount: amount.trim(), recovered: '', issueDate: issueDate.trim() };
      const { result } = await genInvestCert(p.id, co.id, form);
      const dv = JSON.parse(JSON.stringify(p.docver || {})) as DocVerMap;
      const at = new Date().toISOString();
      const ver: DocVer = { label: '자동 v' + ((dv[KEY] || []).length + 1), doc: '', pdf: result.pdf, kind: 'auto', s3doc: null, s3pdf: result.s3pdf, at };
      dv[KEY] = [...(dv[KEY] || []), ver];
      const patched = mutStore(p, 's11send', (s) => { s.txt = s.txt || {}; s.txt[co.id + '_form'] = JSON.stringify(form); });
      onPatch({ ...patched, docver: dv });
      toast('확인서 생성 완료 — 미리보기·서명에서 확인하세요');
      setFormOpen(false);
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };

  const pv = async (dl?: boolean) => {
    if (busy) return; setBusy('pv');
    try { if (!cur || !cur.s3pdf) { toast('먼저 생성하세요'); return; } if (dl) await saveS3ToProject(p, cur.s3pdf, fileName, toast); else await openS3(cur.s3pdf, fileName, true); }   // ⬇ 원본 = 조합 저장 폴더(2026-11-01)
    catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };

  const replace = async (f: File, kind: 'seal' | 'pdfonly') => {
    setBusy('up');
    try {
      const r = await uploadDoc(p.id, KEY, kind, f);
      const dv = JSON.parse(JSON.stringify(p.docver || {})) as DocVerMap;
      if (!dv[KEY] || !dv[KEY].length) dv[KEY] = cur ? [{ ...cur }] : [];
      const prev = dv[KEY][dv[KEY].length - 1] || { doc: '', pdf: fileName, s3doc: null };
      const n = dv[KEY].length + 1;
      const at = new Date().toISOString();
      const ver: DocVer = kind === 'seal'
        ? { label: '날인본 v' + n, doc: prev.doc, pdf: r.pdf, kind: 'seal', s3doc: prev.s3doc || null, s3pdf: r.s3pdf, at }
        : { label: 'PDF만 교체 v' + n, doc: prev.doc, pdf: r.pdf, kind: 'pdfonly', s3doc: prev.s3doc || null, s3pdf: r.s3pdf, at };
      dv[KEY] = [...dv[KEY], ver];
      onPatch({ docver: dv });
      toast(kind === 'seal' ? '날인본 교체됨' : 'PDF만 교체됨');
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };

  const openSign = async () => {
    if (busy) return;
    if (!cur || !cur.s3pdf) { toast('먼저 확인서를 생성하세요'); return; }
    setBusy('sign');
    try {
      try { localStorage.removeItem('pdf_edit_url'); } catch (e) { /* noop */ }
      const blob = await fetchFileBlob(cur.s3pdf, fileName);
      setEditUrl(URL.createObjectURL(blob));
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const closeEditor = () => { if (editUrl) URL.revokeObjectURL(editUrl); setEditUrl(null); };

  const IN: React.CSSProperties = { maxWidth: 140 };
  return (
    <>
      <div className="s3file pdf">
        <span className="rf">📕 {fileName}</span>
        {cur && cur.kind === 'seal' ? <span className="pill p-blue">✍ 서명 완료파일</span> : real ? <span className="pill p-gray">PDF 변환됨</span> : <span className="pill p-gray">미생성</span>}
        <span className="grow" />
        <button className="rbtn" onClick={() => setFormOpen((o) => !o)}>🧾 확인서 입력 {formOpen ? '▲' : '▼'}</button>
        <button className="rbtn" disabled={!!busy || !real} onClick={() => pv()}>👁 미리보기</button>
        <button className="rbtn" disabled={!!busy || !real} onClick={() => pv(true)}>⬇ 원본</button>
        <button className="rbtn" disabled={!!busy} onClick={openSign} title="조합인감 날인 → 날인본으로 저장">✍ 서명</button>
        {more && <UpLabel text={busy === 'up' ? '업로드 중…' : '날인본 업로드'} disabled={!!busy} onUp={(f) => replace(f, 'seal')} />}
        <button className="rbtn" onClick={() => setMore((m) => !m)} title="날인본 업로드">{more ? '✕ 닫기' : '⋯ 기타'}</button>
      </div>
      {formOpen && (
        <div style={box}>
          <div style={row}>
            {TYPE_DEFS.map((t) => (
              <label key={t.k} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}>
                <input type="radio" checked={type === t.k} onChange={() => pickType(t.k)} />{t.label}
              </label>
            ))}
          </div>
          <div style={row}>
            <input className="cotext" style={IN} value={subType} onChange={(e) => setSubType(e.target.value)} placeholder="투자유형 표기" />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input className="cotext" style={IN} value={payDate} onChange={(e) => setPayDate(dotDate(e.target.value))} placeholder="입금일 2026.07.01." title="입금일 = 등기부등본상 사채 발행일" />
              <button type="button" className="rbtn" onClick={() => setRegOpen(true)} title="9단계 등기부등본(전환사채 발행 후)을 보며 입금일 적기">📄 등기부 보며</button>
              {!payDate && cbIssue.length === 11 && <button type="button" className="rbtn" onClick={() => setPayDate(cbIssue)} title="8단계 사채 서류의 발행일">↳ {cbIssue}</button>}
            </span>
            <input className="cotext" style={IN} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="투자금액(원)" inputMode="numeric" />
            {type === 'pref' && <input className="cotext" style={{ maxWidth: 100 }} value={shares} onChange={(e) => setShares(e.target.value)} placeholder="주식수" inputMode="numeric" />}
            {type === 'cb' && (
              <select className="cotext" style={{ maxWidth: 110 }} value={secured} onChange={(e) => setSecured(e.target.value)}>
                <option value="부">담보 부</option><option value="여">담보 여</option>
              </select>
            )}
            <input className="cotext" style={{ maxWidth: 120 }} value={issueDate} onChange={(e) => setIssueDate(dotDate(e.target.value))} placeholder="발급일" />
          </div>
          <div style={row}>
            <span style={{ fontSize: 11, color: '#94A3B8' }}>서명란 자동: {p.org} ({p.gp}) (인) · 한국벤처캐피탈협회장 귀하</span>
            <span className="grow" />
            <button className="btn pri sm" disabled={!!busy} onClick={gen}>{busy === 'gen' ? '생성 중…' : real ? '♻ 확인서 재생성' : '📄 확인서 생성'}</button>
          </div>
        </div>
      )}
      {regOpen && (
        <DocFieldModal
          title={(co.name || '법인') + ' · 등기부등본 (전환사채 발행 후)'}
          fileName={reg.name}
          load={docLoader(p, reg) || undefined}
          noneMsg="9단계 「법인별 구비서류」에 등기부등본(전환사채 발행 후)을 올리면 여기서 보며 적을 수 있습니다"
          fields={[{ key: 'payDate', label: '입금일 (등기부등본상 사채 발행일)', value: payDate, hint: '2026.07.01.', onChange: (v) => setPayDate(dotDate(v)) }]}
          index={0}
          onIndex={() => undefined}
          onClose={() => setRegOpen(false)}
        />
      )}
      {editUrl ? <PdfEditor fileUrl={editUrl} onSaveBlob={async (blob: Blob) => { await replace(new File([blob], fileName, { type: 'application/pdf' }), 'seal'); }} onSaved={closeEditor} onClose={closeEditor} /> : null}
    </>
  );
}
